import {
    ParagraphClass,
    Sentence,
    SimpleString,
    isRenderItem,
    isRenderable,
    isSlippable,
    slip,
    type Renderable,
    type RenderItem,
    PageClass,
} from "@circleofa/acim-engine"
import { derived, get, writable } from "svelte/store"
import { acim } from "@utils/acim"
import { createFirebaseCache } from "@utils/localStorageFirebase"
import { cancelDebounceMergeQueue, debounceMergeQueue } from "@utils/helpers/debounce"
import { randomString } from "@utils/helpers/random-string"
import { ObjectEntries } from "@utils/helpers/object-entries"
import { awaitStore } from "@utils/helpers/await-store"
import { createPrompt } from "@components/bottomPopup/Prompt.module"
import { goto } from "$app/navigation"
import { page } from "$app/stores"
import { convertRangePathToURLPath, isRangePathURLable } from "@utils/fallback-book-redirection"
import { altKeyPressed, ctrlCmdKeyPressed, registerHotKey } from "@utils/underhood/registerHotkey"
import { viewingNotationPage } from "../routes/book/[notation]/+page.svelte"
import { copyHtml } from "@utils/reading/copy"
import { tooltip } from "@utils/tooltip"
import { extractEmoji, getTabDisplayName, tabNameWithoutEmoji } from "@utils/helpers/tab-name"

export { extractEmoji } from "@utils/helpers/tab-name"

/**
 * @file Manages tracking of pages in the database
 *
 *
 * It's really important to keep in mind the possibility of an infinite loop
 * especially with tab conflict management. If one device changes tab while
 * you're using another one, and the new one prompts you to update it, yet it sets
 * the old one in some way and that submits it to the database, it might create a
 * kind of ping pong between devices.
 */

/// Local storage keys for the time bean
const TABS_KEY = "tabs" as const
const TABS_DEBOUNCE_KEY = "tabs" as const
const ABANDONED_TAB = "abandoned" as const

// Used to let the app know if the tabs are ready and loaded
export const tabsReady = writable(false)

// Turn on when moving to tab to process page changes differently
// Set this to true momentarily on redirection
export const movingToTab = writable(false)

// Contains the data of the current page
export const currentPageOrRenderItemPath = writable("" as string)

export function unsetcurrentPageOrRenderItemPath() {
    currentPageOrRenderItemPath.set("")
}

/**
 * Gives access to the current page and tab regardless of where you
 * are on the screen
 */
export const currentPageOrRenderItem = derived<
    typeof currentPageOrRenderItemPath,
    Renderable | SimpleString | ParagraphClass | null
>(currentPageOrRenderItemPath, (path) => {
    if (!path) return null
    try {
        const item = acim.find(path)
        if (
            !isRenderable(item) &&
            !(item instanceof SimpleString) &&
            !(item instanceof ParagraphClass)
        ) {
            throw new Error("Item not instance of allowed")
        }
        return item
    } catch (e) {
        return null
    }
})

/**
 * Gets the currently selected page based on
 * {@link currentPageOrRenderItem} and also
 * {@link currentPageOrRenderItemPath}
 */
export const currentPage = derived<typeof currentPageOrRenderItem, Renderable | null>(
    currentPageOrRenderItem,
    (page) => {
        if (!page) return null
        if (isRenderable(page)) return page
        return page.renderParent
    },
)

registerHotKey("0", (down) => {
    if (get(currentPage) && down) {
        const text = `Read on the ACIM CE App:`
        const url = `https://acimce.app/:${get(currentPage)!.path}`
        copyHtml(`${text} <a href="${url}">${url}</a>`, `${text} ${url}`)
        tooltip("Copied!", false, false)
    }
})

export const lastTab = writable<TabClass | null>(null)

type LocalStorageTabs = TabObject[]
type TabData = {
    selectedTab: number
    tabs: LocalStorageTabs
}

type ScrollMemory = {
    environment: string
    position: number
}
type TabObject = {
    name: string
    path: string
    scrollMemory?: ScrollMemory
}
export type TabID = `_${string}`
type TabsStored = {
    [id: TabID]: TabObject
    selectedTab: TabID | null
}

/**
 * A TabPath is a type of path that is either
 * a renderable, or a renderItem. This ensures that
 * there are never any weird artifacts in the tab path
 * storage, like ranges and such.
 */
export type TabPath = string

/**
 * Gets the url based on the path
 * @param path Full path (ie, T.3.IV.5:2)
 * @returns URL plus hash of path (ie, T.3.IV#T.3.IV.5:2)
 * @throws if path is neither renderable nor slippable
 */
export function urlFromPath(path: string): string {
    return convertRangePathToURLPath(path)

    // const found = acim.find(path)
    // if (!found) {
    //     throw new Error("Path not found: " + path)
    // }
    // if (isRenderable(found)) {
    //     return path
    // }
    // if (isSlippable(found)) {
    //     return slip(found).path + "#" + path
    // }
    // throw new Error("Path found but not renderable or slippable: " + path)
}

let conflictDialogueOpen = false

//
//  REGEX STUFF
//

/**
 * Returns path if it's a path, returns In if it doesn't match
 * any recovery pattern
 * @param path
 * @returns
 */
function attemptPathRecovery(path: string): TabPath {
    if (isTabPath(path)) return path

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const [first, second] = path.split("#")
    if (isTabPath(second)) return second
    if (isTabPath(first)) return first
    return "In"
}

export class TabClass {
    #path: TabPath
    #name: string

    /**
     *
     * @param path
     * @param name
     * @param id
     * @param scrollMemory
     * @throws if TabPath is invalid {@link InvalidTabPathError}
     */
    constructor(
        path: string,
        name: string,
        public id: TabID,
        public scrollMemory: ScrollMemory | undefined,
    ) {
        this.#path = attemptPathRecovery(path)
        this.#name = name
    }

    /**
     * Retrieve a formatted URL for going to this place
     * straight in the book
     * @throws if url does not work
     */
    get url() {
        return urlFromPath(this.#path)
    }

    get emoji() {
        return extractEmoji(this.name)
    }

    get nameWithoutEmoji() {
        return tabNameWithoutEmoji(this.name)
    }

    get displayName() {
        return getTabDisplayName(this.name)
    }

    get renderablePage() {
        const found = acim.find(this.#path)
        if (!found) {
            return null
        }
        if (isRenderable(found)) {
            return found
        }
        if (isSlippable(found)) {
            return slip(found)
        }
        return null
    }

    // set url(url : string) {
    //     [this.page, this.path] = TabClass.splitPath(url)
    // }

    toFragment(): { [x: string]: TabObject } {
        const returnObj: TabObject = {
            name: this.#name,
            path: this.#path,
        }
        if (this.scrollMemory) returnObj.scrollMemory = { ...this.scrollMemory }
        return { [this.id]: returnObj }
    }

    /**
     * This selects and updates
     */
    select() {
        selectTab(this.id)
    }

    get name() {
        return this.#name
    }

    set name(name: string) {
        this.#name = name
        tabs.update((r) => r)
        // this.save(true)
    }

    get path() {
        return this.#path
    }

    /**
     * Sets the path for the tab.
     * Make sure you catch this or check to make sure the
     * path is a {@link TabPath}
     * @throws if path is not tab path {@link InvalidTabPathError}
     */
    set path(path: string) {
        if (!isTabPath(path)) throw new InvalidTabPathError(path)
        this.#path = path
        tabs.update((r) => r)
    }

    /**
     * This deletes but doesn't update, call this.save() to update
     */
    delete() {
        deleteTab(this.id)
    }

    matchesEnvironment() {
        if (!this.scrollMemory?.environment?.length) return false
        return TabClass.getEnvironment() == this.scrollMemory.environment
    }

    static getEnvironment(): string {
        return "" + window.innerWidth
    }

    saveScroll(scrollDistance: number | undefined) {
        if (scrollDistance === undefined) {
            this.scrollMemory = undefined
            return
        }
        this.scrollMemory = {
            environment: TabClass.getEnvironment(),
            position: scrollDistance,
        }

        // Debounced save
        this.save(false)
    }

    static splitPath(path: string): [page: string, path: string] {
        const args = path.split("#")
        return [args[0], args[1] ? args[1] : ""]
    }

    /**
     * Creates a tab instance from a firebase object
     * @param id
     * @param obj
     * @returns
     * @throws if invalid object path
     */
    static fromObject(id: TabID, obj: TabObject): TabClass {
        const tab = new TabClass(obj.path, obj.name, id, obj.scrollMemory)
        return tab
    }

    /**
     * Update this class instance from a tab object
     * Make sure you check to make sure the path is a proper {@link TabPath}
     * @param obj TabOject containing tab data
     * @returns {boolean} true if changed
     * @throws if the name is not URLable {@link InvalidTabPathError}
     */
    updateFromObject(obj: TabObject): boolean {
        if (!isTabPath(obj.path)) {
            obj.path = attemptPathRecovery(obj.path)
            // throw new InvalidTabPathError("New object path not a tab path: " + obj.path)
        }
        const changed = this.isDifferent(obj)
        if (changed) {
            this.#name = obj.name
            this.#path = obj.path
        }
        return changed
    }

    isDifferent(obj: TabObject) {
        return this.#name != obj.name || this.#path != obj.path
    }

    async conflictManage(obj: TabObject) {
        if (this.isDifferent(obj)) {
            if (!conflictDialogueOpen) {
                conflictDialogueOpen = true
                createPrompt({
                    headline: "Oh!",
                    detail:
                        "It looks like another device has a newer update on " +
                        obj.path +
                        ". What would you like to do?",
                    action: () => {
                        conflictDialogueOpen = false
                        this.updateFromObject(obj)
                        tabs.update((r) => r)
                        this.save(true) // Just in case some loose scrolling got caught in the interim
                        selectAndGotoTab(this.id)
                    },
                    cancel: () => {
                        conflictDialogueOpen = false
                        // No need to save - I think
                    },
                    buttonLabel: "Go to " + obj.path,
                    cancelButtonlabel: "Stay at " + this.path,
                    type: "warning",
                })
            }
        }
    }

    get scrollAmount() {
        if (this.scrollMemory && this.matchesEnvironment()) return this.scrollMemory.position
        return undefined
    }

    save(saveImmediately: boolean) {
        const saveObj = this.toFragment()
        if (saveImmediately) {
            debounceMergeTabs(saveObj, 1000)
        } else {
            debounceMergeTabs(saveObj)
        }
    }
}

export const tabs = writable(new Map<TabID, TabClass>())

// Resets all data every time someone logs in and out
function initialize() {
    tabs.set(new Map())
    movingToTab.set(false)
    unsetcurrentPageOrRenderItemPath()
    cancelDebounceMergeQueue(TABS_DEBOUNCE_KEY)
    lastTab.set(null)
}

export const tabsDatabase = createFirebaseCache<TabsStored>({
    name: TABS_KEY,
    version: 4,
    def: { selectedTab: null },
    ready: tabsReady,
    reset: initialize,
})

/**
 * Save data
 * @param partial tabs to be saved
 * @returns promise fulfilled when data is set
 */
const mergeSave = (partial: Partial<TabsStored>) => {
    console.log("🧭 Saving tabs...")
    return tabsDatabase.setMerge(partial)
}

/**
 * Abstracts debounce action to tabs
 * @param obj The tab information to be saved
 * @param timer ms to debounce, defaults to 3000
 * @returns void
 */
function debounceMergeTabs(obj: Partial<TabsStored>, timer = 3000) {
    return debounceMergeQueue(TABS_DEBOUNCE_KEY, obj, mergeSave, timer)
}

// null means not loaded, that way we know when to overwrite and when not to
export const selectedTabID = writable<SelectedTab>("abandoned")

export const selectedTab = derived([tabs, selectedTabID], ([tabList, id]) => {
    if (!tabList) return undefined
    if (id == ABANDONED_TAB) return null
    if ([...tabList.keys()].includes(id)) {
        return tabList.get(id)
    }
    return null
})

/**
 * Processes all the tabs from database into a {@link TabsStored} object
 */
tabsDatabase.subscribe((tabDatabase) => {
    if (!tabDatabase) return

    // Save for later
    const databaseSelectedTab = tabDatabase.selectedTab

    const { selectedTab, ...tabObjects } = tabDatabase

    // Only apply if changed
    let changed = false

    // First see if there are any deletions
    const currentTabs = get(tabs)
    const oldKeys = [...currentTabs.keys()]
    const newKeys = Object.keys(tabObjects) as Array<keyof typeof tabObjects>

    // For every old key, if the new key list doesn't include it, delete it from currentTabs
    oldKeys.forEach((oldKey) => {
        if (!newKeys.includes(oldKey)) {
            changed = true
            currentTabs.delete(oldKey)
        }
    })

    // Now let's see if there are any new ones or update the old ones
    ObjectEntries(tabObjects).forEach(([id, tabObject]) => {
        const currentTab = currentTabs.get(id)
        if (currentTab) {
            // Let's see if it's selected, to not create conflict
            if (get(selectedTabID) == id) {
                // It's the same time, conflict manage
                // The name however gets bypassed
                if (currentTab.name != tabObject.name) {
                    currentTab.name = tabObject.name
                    changed = true
                }
                currentTab.conflictManage(tabObject)
            } else {
                // It's matched, let's update
                changed = currentTab.updateFromObject(tabObject) || changed
            }
        } else {
            // No match, create a new one
            const newClass = TabClass.fromObject(id, tabObject)
            currentTabs.set(id, newClass)
            changed = true
        }
    })

    if (changed) {
        tabs.set(currentTabs)
    }

    const lastTabFromDB = (databaseSelectedTab && get(tabs)?.get(databaseSelectedTab)) || null
    if (lastTabFromDB) lastTab.set(lastTabFromDB)

    // if (!get(selectedTabID)) {
    //     console.log("++", get(tabs).get(databaseSelectedTab))
    //     lastTab.set(get(tabs).get(databaseSelectedTab))

    //     // We don't want to update selectedTabID if it's incoming and different from current
    //     // otherwise you can't work on two devices

    //     // selectedTabID.set(databaseSelectedTab)
    // }
})

type SelectedTab = typeof ABANDONED_TAB | TabID

/**
 * Changes selected tab to ID. Saves to database.
 * Pass the "abandoned" to deselect
 * @param id
 * @returns tab if success or null if none
 */
export function selectTab(id: SelectedTab) {
    // Check if tab exists
    if (id == ABANDONED_TAB) {
        selectedTabID.set(ABANDONED_TAB)
        return null
    }
    const exists = get(tabs)?.get(id)
    selectedTabID.set(exists ? id : ABANDONED_TAB)
    if (exists) {
        debounceMergeTabs({ selectedTab: exists.id }, 0)
    }
    return exists || null
}

/**
 * Deletes tab then saves to database - no debounce
 * @param id ID of tab
 */
export function deleteTab(id: TabID) {
    // TabClass will be cleaned up reactively
    debounceMergeTabs({}, 0) // Save all the stuff that has yet to be saved
    tabsDatabase.deleteMerge(id)
}

/**
 * Changes name, this needs to be its own function to prevent infinite loop
 * @param tab TabClass to modify
 * @param name name of new tab
 * @returns true if succeeded
 */
export function setName(tab: TabClass, name: string) {
    tab.name = name

    // Save
    tab.save(true)
    return true
}

/**
 * Sets path in general for either the current tab or just for UI view
 * and then sets currentPageOrRenderItemPath to supplied path
 * Does not update tabs if paths are the same
 * @param path path to set, like T-3.II.3 or T-2
 * @param saveImmediately option to save now instead of giving time for debounce, only relevant if `save` is true
 * @param save saves changes in current tab
 */
export function setPath(path: string, saveImmediately = false, save = true) {
    if (path === undefined) {
        throw new Error("got undefined for set path!")
    }

    // If there is a tab, and we're not going to be saving what is already in store
    // and only if save value is true
    const currentTab = get(selectedTab)
    if (currentTab && save) {
        const storedPath = currentTab.path
        if (storedPath != path) {
            // Update path on client side
            currentTab.path = path

            // Save to database - sometimes this is called from changing a page, and other times from scrolling
            // If scrolling, we'd like to debounce it. Hence the saveImmediately option
            currentTab.save(saveImmediately)
        }
    }

    // Updates the app's current location information
    currentPageOrRenderItemPath.set(path)
}

/**
 * Format the path in such a way that
 * the can take it. E.g. it can't be a range
 * @param page renderable page
 * @param longPath hash without the hashtag, e.g. T-3.II.1:2-3
 */
export function formatTabPath(page: Renderable, longPath: string): string {
    if (!longPath) return page.path
    const longPathItem = acim.find(longPath)
    if (longPathItem) {
        if (isSlippable(longPathItem) && longPathItem.slipDirection == "down") {
            return page.path
        } else {
            if (isTabItem(longPathItem)) {
                return longPathItem.path
            } else {
                throw new Error(
                    `Looks like we got a hit, but it wasn't slippable or a valid TabItem: ${longPath}`,
                )
            }
        }
    }
    const [start, _] = acim.fromRange(longPath)
    if (start) return start.path
    throw new Error(`No page and longPath not rangeable. longPath: ${longPath}`)
}

/**
 * Checks to see if the item is an item that has a path that's an approved {@link TabPath}
 * @param path
 * @returns
 */
export function isTabItem(found: any): found is Renderable | RenderItem | Sentence {
    return (found && isRenderable(found)) || isRenderItem(found) || found instanceof Sentence
}

/**
 * Checks to see if the tab is an approved {@link TabPath}.
 */
export function isTabPath(path: string): path is TabPath {
    try {
        const found = acim.find(path)
        return isTabItem(found)
    } catch {
        return false
    }
}

/**
 * Set the scroll pixel number of the current tab
 * and saves it using debounce to database
 * @param topScroll number
 * @returns void
 */
export function setScroll(topScroll: number | undefined = undefined) {
    const tab = get(selectedTab)
    if (!tab) return
    tab.saveScroll(topScroll) // should save using debounce
}

/**
 * Creates a new tab at path
 * @param path optional path - defaults to current page
 * @param name optional name of tab
 * @returns id of new tab
 */
export async function newTab(path = "", name = "Untitled") {
    path = path.length
        ? path
        : get(currentPageOrRenderItemPath).length
          ? get(currentPageOrRenderItemPath)
          : "Front-1"
    const id = newTabID()

    debounceMergeTabs(
        {
            [id]: {
                path,
                name,
            },
        },
        0,
    )

    // Wait until it's updated
    await awaitStore(tabs, (tabs) => tabs.has(id))

    return id
}

/**
 * Create a random new tab ID that doesn't conflict with current tab
 */
function newTabID(): TabID {
    const keys = Object.keys(get(tabs))
    let str: string
    do {
        str = randomString()
    } while (str in keys)
    return ("_" + str) as TabID
}

export const switchedTabOnSamePlace = writable(1) // just subscribe to this, and when triggered, it means that we want to refresh the page
export function selectAndGotoTab(id: TabID) {
    const tab = selectTab(id)
    if (!tab) return
    goto("/book/" + tab.url)
    movingToTab.set(true) // let notation page know that the next move is a tab movement
    if (get(page).params.notation + location.hash == tab.url) {
        switchedTabOnSamePlace.update((s) => s + 1)
    }
}

//
// ERRORS
//

export class InvalidTabPathError extends Error {
    constructor(public path: string) {
        const message = `Invalid tab path: ${path}`
        super(message)
        this.name = "InvalidTabPathError"
    }
}

//
// Tab hotkey navigation
//

registerHotKey("Tab", (down, e) => {
    if (!down) return
    if (e?.key === "Tab" && e.ctrlKey) {
        if (e.shiftKey) {
            previousTab()
        } else {
            nextTab()
        }
    }
})

function nextTab() {
    const currentTab = get(selectedTab)
    if (!currentTab) return
    const currentTabs = [...get(tabs).values()]
    const idx = currentTabs.findIndex((tab) => currentTab === tab)
    const nextTab = currentTabs[(idx + 1) % currentTabs.length]

    if (get(viewingNotationPage)) {
        selectAndGotoTab(nextTab.id)
    } else {
        selectTab(nextTab.id) // select the next tab
    }
}

function previousTab() {
    const currentTab = get(selectedTab)
    if (!currentTab) return
    const currentTabs = [...get(tabs).values()]
    const idx = currentTabs.findIndex((tab) => currentTab === tab)
    const prevTab = currentTabs[(idx - 1 + currentTabs.length) % currentTabs.length]

    if (get(viewingNotationPage)) {
        selectAndGotoTab(prevTab.id)
    } else {
        selectTab(prevTab.id) // select the previous tab
    }
}
